import type { ServerConnection } from "@/context/server"

const GITHUB_API = "https://api.github.com"
const OWNER = "CastaliaInstitute"
const SERVERS_PATH = "settings/opencode-servers.json"
const BRANCH = "main"
const DEFAULT_REPO_TEMPLATE = "Castalia-{user}"
const TOKEN_PREFIXES = ["github_pat_", "ghp_", "gho_", "ghu_", "ghs_", "ghr_"]

type RemoteServer = { url: string; displayName?: string; username?: string }
type ContentsFile = { content?: string; sha?: string }

export function isCastaliaGithubToken(token: string) {
  return TOKEN_PREFIXES.some((prefix) => token.startsWith(prefix))
}

const githubHeaders = (token: string, extra: Record<string, string> = {}) => ({
  Accept: "application/vnd.github+json",
  Authorization: `Bearer ${token}`,
  "X-GitHub-Api-Version": "2022-11-28",
  ...extra,
})

const decodeBase64 = (value: string) => {
  const bytes = Uint8Array.from(atob(value.replace(/\n/g, "")), (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

const encodeBase64 = (text: string) => {
  let binary = ""
  for (const byte of new TextEncoder().encode(text)) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export async function verifyCastaliaGithubToken(input: { token: string; repoTemplate?: string }) {
  const userResponse = await fetch(`${GITHUB_API}/user`, { headers: githubHeaders(input.token) })
  if (!userResponse.ok) throw new Error(`Castalia token rejected (${userResponse.status})`)
  const body = (await userResponse.json()) as { login?: string }
  if (!body.login) throw new Error("Castalia token has no GitHub user")
  const repo = `${OWNER}/${(input.repoTemplate || DEFAULT_REPO_TEMPLATE).replace("{user}", body.login)}`
  const repoResponse = await fetch(`${GITHUB_API}/repos/${repo}`, { headers: githubHeaders(input.token) })
  if (!repoResponse.ok) throw new Error(`Castalia data repo ${repo} is not accessible (${repoResponse.status})`)
  return { user: body.login, repo }
}

export function createCastaliaGithubServerStore(input: { token: string; repoTemplate?: string }) {
  let pending: Promise<void> = Promise.resolve()
  let repoCache: string | undefined

  const resolveRepo = async () => {
    repoCache ??= (await verifyCastaliaGithubToken(input)).repo
    return repoCache
  }

  const parse = (json: string) => {
    try {
      const body = JSON.parse(json) as { servers?: RemoteServer[] }
      return (body.servers ?? []).map((server): ServerConnection.Http => ({
        type: "http",
        ...(server.displayName ? { displayName: server.displayName } : {}),
        http: { url: server.url, ...(server.username ? { username: server.username } : {}) },
      }))
    } catch {
      return []
    }
  }

  const request = (path: string, init?: RequestInit) =>
    fetch(`${GITHUB_API}/repos/${path}`, {
      ...init,
      headers: githubHeaders(input.token, (init?.headers as Record<string, string>) ?? {}),
    })

  const contentsUrl = (repo: string) => `${repo}/contents/${SERVERS_PATH}`

  return {
    async load() {
      const repo = await resolveRepo()
      const response = await request(`${contentsUrl(repo)}?ref=${BRANCH}`)
      if (response.status === 404) return []
      if (!response.ok) throw new Error(`Castalia server list failed (${response.status})`)
      const file = (await response.json()) as ContentsFile
      if (!file.content) return []
      return parse(decodeBase64(file.content))
    },
    save(servers: ServerConnection.Http[]) {
      const next = pending.then(async () => {
        const repo = await resolveRepo()
        const body = JSON.stringify(
          {
            servers: servers.map((server) => ({
              url: server.http.url,
              ...(server.displayName ? { displayName: server.displayName } : {}),
              ...(server.http.username ? { username: server.http.username } : {}),
            })),
          },
          null,
          2,
        )
        const current = await request(`${contentsUrl(repo)}?ref=${BRANCH}`)
        if (!current.ok && current.status !== 404)
          throw new Error(`Castalia server list save failed (${current.status})`)
        const sha = current.ok ? ((await current.json()) as ContentsFile).sha : undefined
        const response = await request(contentsUrl(repo), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: "Update opencode server list",
            content: encodeBase64(body),
            ...(sha ? { sha } : {}),
            branch: BRANCH,
          }),
        })
        if (!response.ok) throw new Error(`Castalia server list save failed (${response.status})`)
      })
      pending = next.catch(() => {})
      return next
    },
  }
}
